import { action, flow, makeObservable, observable } from 'mobx';
import { nanoid } from 'nanoid';

import { ApiError, OperationAbortedError } from 'src/packages/errors';
import { equalsDeep } from 'src/packages/utils/equals-deep';

import type { TComplexFormView, TControlView } from './types';
import type { IDirectoriesStorage } from '../directories-storage-service/directories-storage-service';
import type { LocalizationService } from '../localization-service.ts';
import type { INotificationsService } from '../notifications-service';
import type { IMainServices } from 'src/app/types';

import { ControlRulesService } from '../control-rules-service/control-rules-service';

import { serializeDirectoryRecordToSave } from './directory-service.utils';
import { DirectoryRecord } from './entities/directory-record.entity';
import { getControlsViews } from './mappers/get-controls-views';
import { OptionsService } from './options-service';
import { getObjectTypesFromControlView } from './utils';
import { ValidationService } from './validation-service/validation-service';

export class AdditionalDirectoryService {
  private readonly mainServices: IMainServices;
  readonly localizationService: LocalizationService;
  readonly notificationsService: INotificationsService;
  readonly directoriesStorageService: IDirectoriesStorage;
  readonly optionsService: OptionsService;
  readonly validationService: ValidationService;

  readonly view: TComplexFormView;
  readonly directoryName: string;
  readonly entityId: string = nanoid(6);
  readonly controlRulesService: ControlRulesService;
  controlsViewsMap = new Map<string, TControlView>();
  @observable controlViews: TControlView[] = [];
  @observable record: DirectoryRecord;

  @observable isDataFetching = false;
  @observable isAdditionalDirectoriesFetching = false;
  abortControllers = observable.map<string, VoidFunction>();

  constructor(mainServices: IMainServices, directoryName: string, view: TComplexFormView) {
    this.mainServices = mainServices;
    this.directoriesStorageService = mainServices.directoriesStorage;
    this.optionsService = new OptionsService(mainServices.notificationsService, mainServices.directoriesStorage);
    this.localizationService = mainServices.localizationService;
    this.validationService = new ValidationService(mainServices.localizationService);
    this.notificationsService = mainServices.notificationsService;
    this.view = view;
    this.directoryName = directoryName;
    this.controlRulesService = new ControlRulesService(mainServices.directoriesStorage, this.validationService);

    const { recordControls } = getControlsViews(this.view);
    this.controlViews = recordControls;
    this.record = new DirectoryRecord(recordControls, null, -1);

    makeObservable(this);
  }

  @flow.bound
  async *loadAdditionalDirectory(): Promise<void> {
    this.isAdditionalDirectoriesFetching = true;

    if (!this.controlViews.length) {
      return;
    }

    const loadObjects = async (objectType: string) => {
      try {
        await this.directoriesStorageService.loadObjects(objectType, true);
      } catch (e) {
        if (e instanceof OperationAbortedError) {
          return;
        }

        console.error(e);

        if (e instanceof ApiError && e.message) {
          this.notificationsService.showErrorMessage(e.message);
          return;
        }

        this.notificationsService.showErrorMessageT('directory:errors.data.failedToLoadData');
      }
    };

    const requests: Promise<void>[] = [];

    for (const controlView of this.controlViews) {
      // временно отключенный функционал
      // if (columnsVisibility[refObjectType] === false) {
      //   continue;
      // }

      const objectTypes = getObjectTypesFromControlView(controlView);

      objectTypes.forEach((refObjectType) => {
        requests.push(loadObjects(refObjectType));
      });
    }

    await Promise.allSettled(requests);
    yield;

    this.isAdditionalDirectoriesFetching = false;
  }

  @flow.bound
  async *saveChanges(
    additionalRecordValues: Record<string, unknown> | undefined,
    onSuccessSave?: VoidFunction | undefined,
  ): Promise<void> {
    this.isDataFetching = true;

    if (!this.validationService.validateRecord(this.record)) {
      this.notificationsService.showErrorMessageT('directory:errors.validation.invalidRecords');
      this.isDataFetching = false;
      return;
    }

    try {
      await this.directoriesStorageService.createObject(
        this.directoryName,
        serializeDirectoryRecordToSave(this.record, this.controlViews, additionalRecordValues),
      );
      yield;

      onSuccessSave?.();
    } catch (e) {
      yield;

      console.error(e);

      if (e instanceof ApiError && e.message) {
        this.notificationsService.showErrorMessage(e.message);
        return;
      }

      this.notificationsService.showErrorMessageT('directory:errors.data.failedToUpdateDirectory');
    } finally {
      this.isDataFetching = false;
    }
  }

  @action.bound
  onControlBlur(fieldId: string): void {
    this.validationService.validateField(this.record, fieldId);
  }

  @action.bound
  onValueChange(fieldId: string, value: unknown): void {
    if (!this.record) {
      return;
    }

    if (equalsDeep(this.record.data[fieldId], value)) {
      return;
    }

    const { [fieldId]: fieldErrors, ...rest } = this.record.fieldsErrors;
    this.record.updateFieldsErrors(rest);
    this.record.updateData({ [fieldId]: value });

    this.controlRulesService.onControlValueChange(
      this.record,
      this.controlsViewsMap,
      fieldId,
      value,
      (_: DirectoryRecord, fieldId: string, value: unknown) => {
        this.onValueChange(fieldId, value);
      },
    );
  }

  @action.bound
  initializeControls() {
    this.controlRulesService.mapRulesFromControlViews(this.controlViews);

    this.controlViews.forEach((controlView) => {
      this.controlsViewsMap.set(controlView.fieldId, controlView);

      const localeKey = `labels:${controlView.fieldId}.label`;
      const defaultLabel = controlView.defaultLabel || controlView.defaultValue;

      if (!this.localizationService.i18n.exists(localeKey) && defaultLabel) {
        this.localizationService.i18n.languages.forEach((lang) => {
          this.localizationService.i18n.addResource(lang, 'labels', `${controlView.fieldId}.label`, defaultLabel);
        });
      }
    });

    this.loadAdditionalDirectory();

    this.optionsService.createInitialOptions(this.controlViews);
    this.validationService.setControlViews(this.controlViews);
    this.validationService.setRequiredFields(this.view.required);
  }

  init = (): VoidFunction => {
    const optionsServiceDisposer = this.optionsService.init();
    this.initializeControls();

    return () => {
      optionsServiceDisposer();
      this.abortControllers.forEach((fn) => fn());
      this.abortControllers.clear();
    };
  };
}
